import { ApiError } from '../api.ts';
import { VoiceError } from './errors.ts';

// Voorlezen: audio komt als mp3 van de server (die de ElevenLabs-sleutel heeft) en speelt via één
// gedeeld <audio>-element. Op iOS mag audio alleen starten binnen een tik; primeAudio() speelt daarom
// in het klikgebaar een korte stilte op dat element, waarna datzelfde element later (als de mp3 na
// seconden binnen is) wél mag spelen. Zinnen volgen elkaar op: een nieuwe zin stopt eerst de vorige, en
// stop() geeft het gedeelde element meteen (synchroon) vrij, zodat die nieuwe zin in dezelfde tick het
// ontgrendelde element krijgt; een eigen element zou iOS buiten een tik weigeren. Is het gedeelde element
// echt bezet (twee zinnen tegelijk), dan krijgt de tweede een eigen element.
// Korte zinnen blijven even in het geheugen ("herhaal" en de volgende mail halen niets opnieuw op).

export type PlaybackEnd = 'ended' | 'interrupted';

export interface Playback {
  /** Klaar met afspelen (ook na stop()), of onderbroken door de browser zelf; een fout van server of browser komt hier als afwijzing uit. */
  done: Promise<PlaybackEnd>;
  /** De mp3 is binnen en het afspelen begint (of het voorlezen is voor die tijd gestopt of mislukt). */
  started: Promise<void>;
  stop(): void;
}

/** Een stille wav van 60 bytes (8 kHz, mono, 8 samples): genoeg om audio te ontgrendelen. */
const SILENCE = 'data:audio/wav;base64,UklGRjQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YRAAAAAAAAAAAAAAAAAAAAAAAAAA';
/** Pauzeert het element buiten ons om (vergrendeld scherm, telefoontje, Siri), dan zo lang wachten op ended voordat de zin als onderbroken geldt. */
export const INTERRUPT_GRACE_MS = 300;
/** Meldt het element dat het hapert (stalled) en komt de tijd daarna zo lang niet vooruit, dan is de zin ook onderbroken. */
export const STALL_GRACE_MS = 1500;
const CACHE_MAX = 6;

let shared: HTMLAudioElement | null = null;
let primed = false;
/**
 * De zin die het gedeelde element nu heeft (of null als het vrij is). Elke speak() krijgt een eigen token:
 * zo kan de late afronding van een al gestopte zin het element van een nieuwere zin niet vrijgeven of
 * haar handlers wissen.
 */
let owner: symbol | null = null;
const cache = new Map<string, Blob>();
const inflight = new Map<string, Promise<Blob>>();

/** Het gedeelde audio-element; maakt het de eerste keer aan. Null zonder <audio> (tests zonder browser). */
export function sharedAudio(): HTMLAudioElement | null {
  if (typeof Audio === 'undefined') return null;
  if (!shared) {
    shared = new Audio();
    shared.preload = 'auto';
    // Op de iPhone anders schermvullend.
    shared.setAttribute('playsinline', '');
  }
  return shared;
}

/**
 * Ontgrendelt audio: synchroon aanroepen in het klikgebaar dat de spraakmodus opent. Speelt een stilte op het
 * gedeelde element; lukt dat niet (geen gebaar, geen geluid), dan zwijgt het en probeert speak() het gewoon.
 */
export function primeAudio(): void {
  const audio = sharedAudio();
  if (!audio || owner) return;
  try {
    audio.src = SILENCE;
    const p = audio.play() as Promise<void> | undefined;
    if (p && typeof p.then === 'function') p.then(() => void (primed = true), () => undefined);
    else primed = true;
  } catch {
    // geen audio mogelijk
  }
}

/** Of het ontgrendelen (of een eerdere zin op het gedeelde element) gelukt is. */
export function audioPrimed(): boolean {
  return primed;
}

/** Voor tests: element, geheugen en toestand vergeten. */
export function resetAudioForTests(): void {
  shared = null;
  primed = false;
  owner = null;
  cache.clear();
  inflight.clear();
}

/** Haalt de mp3 voor een stuk tekst op; een fout van de server komt als ApiError (tekst en status), zodat describeVoiceError weet wat er is. */
export async function fetchSpeech(text: string, signal?: AbortSignal): Promise<Blob> {
  const r = await fetch('/api/voice/tts', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ text }), ...(signal ? { signal } : {}) });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(body.error ?? `de server gaf status ${r.status}`, r.status);
  }
  return r.blob();
}

/** De mp3 van een korte zin, uit het geheugen als die er al is of al onderweg is; de laatste zes blijven bewaard. */
export function cachedSpeech(text: string, signal?: AbortSignal): Promise<Blob> {
  const hit = cache.get(text);
  if (hit) {
    cache.delete(text);
    cache.set(text, hit);
    return Promise.resolve(hit);
  }
  const running = inflight.get(text);
  if (running) return running;
  const p = fetchSpeech(text, signal)
    .then((blob) => {
      cache.set(text, blob);
      for (const key of cache.keys()) {
        if (cache.size <= CACHE_MAX) break;
        cache.delete(key);
      }
      return blob;
    })
    .finally(() => inflight.delete(text));
  inflight.set(text, p);
  return p;
}

/** Haalt de audio van een zin alvast op (stil), zodat die straks meteen klinkt. */
export function prefetchSpeech(text: string): void {
  if (!text || cache.has(text) || inflight.has(text)) return;
  cachedSpeech(text).catch(() => undefined);
}

/** Het gedeelde element als dat vrij is (dan is `token` de eigenaar), anders een eigen element voor deze ene zin. */
function claim(token: symbol): { audio: HTMLAudioElement; isShared: boolean } {
  const audio = sharedAudio();
  if (audio && !owner) {
    owner = token;
    return { audio, isShared: true };
  }
  if (typeof Audio === 'undefined') throw new VoiceError('unsupported', 'geen audio in deze omgeving');
  return { audio: new Audio(), isShared: false };
}

function clearHandlers(audio: HTMLAudioElement): void {
  audio.onended = null;
  audio.onerror = null;
  audio.onpause = null;
  audio.onstalled = null;
}

export function speak(text: string, opts: { signal?: AbortSignal } = {}): Playback {
  let stopped = false;
  const controller = new AbortController();
  opts.signal?.addEventListener('abort', () => controller.abort(), { once: true });
  let markStarted: () => void = () => undefined;
  const started = new Promise<void>((resolve) => {
    markStarted = resolve;
  });
  const token = Symbol('speak');
  let claimed: { audio: HTMLAudioElement; isShared: boolean } | null = null;
  /** Of deze zin haar element nog heeft: een eigen element altijd, het gedeelde alleen zolang zij de eigenaar is. */
  const owns = () => claimed !== null && (!claimed.isShared || owner === token);
  /** Laat het element los (handlers weg; het gedeelde element weer vrij). Niets als een nieuwere zin het al heeft. */
  const letGo = () => {
    if (!claimed || !owns()) return;
    clearHandlers(claimed.audio);
    if (claimed.isShared) owner = null;
  };
  const release = () => {
    markStarted();
    letGo();
  };
  const done = (async (): Promise<PlaybackEnd> => {
    claimed = claim(token);
    const { audio, isShared } = claimed;
    let blob: Blob;
    try {
      blob = await cachedSpeech(text, controller.signal);
    } catch (e) {
      // Bewust gestopt (stop() of het signaal van buiten): geen fout, gewoon klaar.
      if (stopped || controller.signal.aborted) return 'ended';
      throw e;
    }
    if (stopped) return 'ended';
    const url = URL.createObjectURL(blob);
    audio.src = url;
    try {
      return await new Promise<PlaybackEnd>((resolve, reject) => {
        audio.onended = () => resolve('ended');
        audio.onerror = () => reject(new VoiceError('playback', 'audio kon niet worden afgespeeld'));
        // Een pauze die niet van ons komt en waar geen ended op volgt: de browser heeft de zin afgebroken.
        audio.onpause = () => {
          setTimeout(() => {
            if (!stopped && audio.paused && !audio.ended) resolve('interrupted');
          }, INTERRUPT_GRACE_MS);
        };
        audio.onstalled = () => {
          const at = audio.currentTime;
          setTimeout(() => {
            if (!stopped && !audio.ended && audio.currentTime === at) resolve('interrupted');
          }, STALL_GRACE_MS);
        };
        controller.signal.addEventListener('abort', () => resolve('ended'), { once: true });
        markStarted();
        const p = audio.play() as Promise<void> | undefined;
        if (p && typeof p.then === 'function') {
          p.then(
            () => {
              if (isShared) primed = true;
            },
            (e: Error) => reject(e),
          );
        }
      });
    } finally {
      URL.revokeObjectURL(url);
    }
  })().finally(release);
  return {
    done,
    started,
    stop() {
      stopped = true;
      controller.abort();
      // Synchroon loslaten: een zin die hierna in dezelfde tick begint krijgt het gedeelde element.
      // Is het al van een nieuwere zin (na een gewoon einde), dan blijft het met rust.
      if (!claimed || !owns()) return;
      const { audio } = claimed;
      letGo();
      audio.pause();
      audio.src = '';
    },
  };
}
